import { observer } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import EChartsReact from 'echarts-for-react';
import Switch from '../../../../../FormComponents/Switch'

export default observer(function AgeDistribution({ authentications, values, setFieldValue, advSideScenario }) {

  const ages = useMemo(() => {
    if (authentications.length === 0) return null;
    
    const combinedAges = {};
    authentications.forEach(auth => {
      const authAges = auth?.audience_information?.detailed_audience_stats?.ages;
      if (!authAges) return;
      
      Object.entries(authAges).forEach(([ageGroup, value]) => {
        combinedAges[ageGroup] = (combinedAges[ageGroup] || 0) + value;
      });
    });
    
    // Calculate average for each age group
    Object.keys(combinedAges).forEach(ageGroup => {
      combinedAges[ageGroup] = combinedAges[ageGroup] / authentications.length;
    });
    
    return combinedAges;
  }, [authentications]);

  const topAgeGroup = useMemo(() => {
    return ages && Object.keys(ages)?.reduce((max, group) => ages[max] > ages[group] ? max : group, '');
  }, [ages]);

  const genderByAge = useMemo(() => {
    if (authentications.length === 0) return null;
    
    const combinedGenderByAge = {};
    
    authentications.forEach(auth => {
      const authGenderByAge = auth?.audience_information?.detailed_audience_stats?.gender_by_age;
      if (!authGenderByAge) return;
      
      Object.entries(authGenderByAge).forEach(([ageGroup, genderData]) => {
        if (!combinedGenderByAge[ageGroup]) {
          combinedGenderByAge[ageGroup] = { male: 0, female: 0 };
        }
        
        combinedGenderByAge[ageGroup].male += genderData.male || 0;
        combinedGenderByAge[ageGroup].female += genderData.female || 0;
      });
    });
    
    Object.keys(combinedGenderByAge).forEach(ageGroup => {
      combinedGenderByAge[ageGroup].male = combinedGenderByAge[ageGroup].male / authentications.length;
      combinedGenderByAge[ageGroup].female = combinedGenderByAge[ageGroup].female / authentications.length;
    });
    
    return combinedGenderByAge;
  }, [authentications]);

  const option = useMemo(() => ({
    xAxis: {
      data: [
        `13-17${genderByAge ? `\n\n${(genderByAge?.[`13-17`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`13-17`]?.female * 100 || 0).toFixed(1)}% F` : ''}`, 
        `18-24${genderByAge ? `\n\n${(genderByAge?.[`18-24`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`18-24`]?.female * 100 || 0).toFixed(1)}% F` : ''}`, 
        `25-34${genderByAge ? `\n\n${(genderByAge?.[`25-34`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`25-34`]?.female * 100 || 0).toFixed(1)}% F` : ''}`, 
        `35-44${genderByAge ? `\n\n${(genderByAge?.[`35-44`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`35-44`]?.female * 100 || 0).toFixed(1)}% F` : ''}`, 
        `45-64${genderByAge ? `\n\n${(genderByAge?.[`45-64`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`45-64`]?.female * 100 || 0).toFixed(1)}% F` : ''}`, 
        `65+${genderByAge ? `\n\n${(genderByAge?.[`65+`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`65+`]?.female * 100 || 0).toFixed(1)}% F` : ''}`
      ],
      axisLabel: {
        interval: 0,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    yAxis: {
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false }
    },
    series: [
      {
        // Total age distribution bar (background)
        type: 'bar',
        z: 0,
        barWidth: '60%',
        data: ['13-17', '18-24', '25-34', '35-44', '45-64', '65-'].map(ageGroup => ({
          value: +(ages?.[ageGroup] * 100).toFixed(2),
          label: { show: true, position: 'top', formatter: '{c}%' },
          itemStyle: {
            barBorderRadius: [10, 10, 0, 0],
            borderWidth: 1,
            borderType: 'solid',
            borderColor: '#C5CCCB',
            color: '#C5CCCB',
          }
        }))
      },
      {
        // Female distribution
        type: 'bar',
        stack: 'gender',
        z: 1,
        barWidth: '60%',
        barGap: '-100%',
        data: ['13-17', '18-24', '25-34', '35-44', '45-64', '65+'].map(ageGroup => ({
          value: +(genderByAge?.[ageGroup]?.female * 100 || 0).toFixed(1),
          itemStyle: { color: '#2CCDB4' },
          emphasis: {
            label: { 
              show: true,
              position: 'inside',
              formatter: '{c}% F'
            }
          }
        }))
      },
      {
        // Male distribution
        type: 'bar',
        stack: 'gender',
        z: 1,
        barWidth: '60%',
        barGap: '-100%', 
        data: ['13-17', '18-24', '25-34', '35-44', '45-64', '65+'].map(ageGroup => ({
          value: +(genderByAge?.[ageGroup]?.male * 100 || 0).toFixed(1),
          itemStyle: { color: '#C1F6EE', barBorderRadius: [10, 10, 0, 0] },
          emphasis: {
            label: { 
              show: true,
              position: 'inside',
              formatter: '{c}% M'
            }
          }
        }))
      },
    ],
    grid: {
      left: 20,
      right: 20,
      top: 30,
      bottom: '0%',
      containLabel: true
    },
    textStyle: {
      fontSize: 14
    },
  }), [ages, genderByAge])

  return (
    <div className="w-full h-full flex bg-white dark:bg-rb-dark-mode p-4 rounded-lg col-span-3">
      <div className="w-1/4 border-r-1 border-gray-200 dark:border-rb-dark-mode-lighter mr-4 flex pr-6 flex-col justify-between py-7">
        <div className="flex gap-4 items-center mb-4">
          <p className={`font-bold text-xl dark:text-white ${advSideScenario || values.audience_age_visible ? '' : 'opacity-30'}`}>Age Distribution</p>
          {!advSideScenario && <Switch name="audience_age_visible" value={values.audience_age_visible} onChange={({ target: { checked }}) => setFieldValue('audience_age_visible', checked, true)} eyeSwitch />}
        </div>
        <div className={`w-full p-4 flex flex-col items-center justify-center bg-gray-100 dark:bg-rb-dark-mode-lighter rounded-xl ${advSideScenario || values.audience_age_visible ? '' : 'opacity-30'}`}>
          <p className="font-bold text-gray-300 dark:text-gray-400">Top age group</p>
          <p className="font-bold text-5xl dark:text-white">{topAgeGroup}</p>
        </div>
      </div>
      <div className="w-full h-full">
        <EChartsReact 
          option={option} 
          style={{ width: '100%', height: '350px', opacity: (advSideScenario || values.audience_age_visible) ? 1 : 0.3 }} 
        />
      </div>
    </div>
  )
})
